/**
 * ObjectInstanceRegistrationService is a service class responsible for registering, managing, and providing access to object instances.
 * It maintains a registry of objects, their UUIDs, and their names, and provides methods to interact with these objects.
 */
class ObjectInstanceRegistrationService {
    /**
     * Constructs an instance of ObjectInstanceRegistrationService.
     * @param {Object} $signals - The signals service for emitting events.
     * @param {Object} $bridges - The bridges service for accessing various utilities.
     * @param {Object} $route - The route service for routing purposes.
     * @param {Object} $q - The promise service for handling asynchronous operations.
     */
    constructor($signals, $bridges, $route, $q) {
        this.$signals = $signals;
        this.$bridges = $bridges;
        this.$route = $route;
        this.$q = $q;

        // Meta setup
        this._logIdentifier = 'ObjectInstanceRegistrationService';
        this.$logger = this.$bridges.logger();
        this.$logger.info(this, 'initialize');

        // Local variables
        this.knownObjects = {};
        this.knownObjectUUIDsByType = {};
        this.objectNames = {};
        this.promises = {};
    }

    /**
     * Checks if an array contains a specific object and returns its index.
     * @param {Array} a - The array to search in.
     * @param {Object} obj - The object to search for.
     * @returns {number} - The index of the object in the array, or -1 if not found.
     */
    arrayContainsGetIndex(a, obj) {
        let i = a.length;

        while (i--) {
            if (a[i] === obj) {
                return i;
            }
        }

        return -1;
    }

    /**
     * Checks if an array contains a specific object.
     * @param {Array} a - The array to search in.
     * @param {Object} obj - The object to search for.
     * @returns {boolean} - True if the object is found in the array, otherwise false.
     */
    arrayContains(a, obj) {
        return this.arrayContainsGetIndex(a, obj) >= 0;
    }

    /**
     * Retrieves all known objects.
     * @returns {Object} - An object containing all known objects.
     */
    getObjects() {
        return this.knownObjects;
    }

    /**
     * Retrieves all known object names.
     * @returns {Object} - An object containing all known object names.
     */
    getObjectNames() {
        return this.objectNames;
    }

    /**
     * Retrieves all UUIDs of objects of a specific type.
     * @param {string} objectType - The type of object to retrieve UUIDs for.
     * @returns {Array} - An array of UUIDs for the specified object type.
     */
    getObjectUUIDsByType(objectType) {
        let t = this.knownObjectUUIDsByType[objectType];

        if (typeof t === 'undefined') {
            this.knownObjectUUIDsByType[objectType] = t = [];
        }

        return t;
    }

    /**
     * Retrieves all named UUIDs of objects of a specific type.
     * @param {string} objectType - The type of object to retrieve named UUIDs for.
     * @returns {Object} - An object mapping UUIDs to names for the specified object type.
     */
    getNamedObjectUUIDsByType(objectType) {
        let t = this.getObjectUUIDsByType(objectType);

        let tn = {};
        t.forEach(uuid => {
            if (this.objectNames.hasOwnProperty(uuid)) {
                tn[uuid] = this.objectNames[uuid];
            }
        });

        return tn;
    }

    /**
     * Retrieves all named objects of a specific type.
     * @param {string} objectType - The type of object to retrieve named objects for.
     * @returns {Object} - An object mapping UUIDs to named objects for the specified object type.
     */
    getNamedObjectsByType(objectType) {
        let t = this.getObjectUUIDsByType(objectType);

        let tn = {};
        t.forEach(uuid => {
            if (this.objectNames.hasOwnProperty(uuid)) {
                tn[uuid] = { 'name': this.objectNames[uuid], 'instance': this.knownObjects[uuid] };
            }
        });

        return tn;
    }

    /**
     * Retrieves an object by its UUID.
     * @param {string} uuid - The UUID of the object to retrieve.
     * @param {boolean} [async=false] - Whether to retrieve the object asynchronously.
     * @returns {Object|Promise} - The object if found, or a promise that will resolve with the object if async is true.
     */
    getObjectByUUID(uuid, async = false) {
        if (!async) {
            return this.knownObjects[uuid];
        }

        let deferred = this.$q.defer();

        if (this.knownObjects.hasOwnProperty(uuid)) {
            deferred.resolve(this.knownObjects[uuid]);
        } else {
            if (!this.promises.hasOwnProperty(uuid)) {
                this.promises[uuid] = [];
            }
            this.promises[uuid].push(deferred);
        }

        return deferred.promise;
    }

    /**
     * Registers an object instance.
     * @param {string} objectType - The type of the object.
     * @param {Object} objectInstance - The object instance to register.
     * @param {string} objectUUID - The UUID of the object.
     * @param {string} [objectName] - The name of the object.
     * @returns {boolean} - True if the object was newly registered, false if it already existed.
     */
    register(objectType, objectInstance, objectUUID, objectName) {
        let objectsForType = this.getObjectUUIDsByType(objectType),
            alreadyExisted = this.arrayContains(objectsForType, objectUUID);

        if (this.promises.hasOwnProperty(objectUUID)) {
            this.promises[objectUUID].forEach(deferred => deferred.resolve(objectInstance));
            delete this.promises[objectUUID];
        }

        if (objectName) {
            this.objectNames[objectUUID] = objectName;
        } else {
            delete this.objectNames[objectUUID];
        }

        this.knownObjects[objectUUID] = objectInstance;
        if (!alreadyExisted) {
            objectsForType.push(objectUUID);
        }

        this.$signals.emit("objectRegistered", objectType, objectInstance, objectUUID, objectName);

        // SMELL: Exposing the service instance to the global window object for debugging purposes.
        window.rrr = this;

        return !alreadyExisted;
    }

    /**
     * Unregisters an object instance.
     * @param {string} objectType - The type of the object.
     * @param {string} objectUUID - The UUID of the object to unregister.
     */
    unregister(objectType, objectUUID) {
        let t = this.getObjectUUIDsByType(objectType);
        let index = t.indexOf(objectUUID);
        if (index > -1) {
            t.splice(index, 1);
        }

        delete this.objectNames[objectUUID];
        delete this.knownObjects[objectUUID];
    }
}

export default ObjectInstanceRegistrationService;